/**
 * @file Compatibility multi-select rendering and modal setup helpers.
 */

import { appData } from '../../app/state/state.js';
import {
    getAssignableUsersWithPermission,
    getSortedUsers,
} from "../utils/userDisplay.js";
import {
    getPatientProviderPickerOptions,
} from "../utils/providerUtils.js";
import {
    getScopedTypeOptions,
} from "../utils/typeScope.js";
import {
    isColorDark,
} from "../utils/displayUtils.js";
import { renderDynamicFieldForm } from './dynamicFieldsModal.js';
import {
    getTaskAssignmentScopeFromParent,
    updateTaskUserSelectors,
} from './linkedEntityPicker.js';
import { updateSelectStyles } from './selectStyles.js';
import { destroyRegisteredControlsIn } from '../ui/controls/controlCore.js';

/**
 * Renders simple multi-select choices as toggle tags.
 * @param {HTMLElement} container Target container.
 * @param {Array} options Available options.
 * @param {string} field Field name.
 * @param {Array<string>} selectedValues Selected values.
 * @param {string | null} displayProp Option display property.
 * @param {string | null} valueProp Option value property.
 * @returns {void}
 */
export function renderMultiSelectChoices(container, options, field, selectedValues, displayProp = null, valueProp = null) {
    if (!container || !Array.isArray(options)) {return;}
    container.innerHTML = options.map(option => {
        const value = valueProp ? option?.[valueProp] : option;
        const displayName = displayProp ? option?.[displayProp] : option;
        if (value === undefined || value === null || displayName === undefined || displayName === null) {return '';}
        const isSelected = Array.isArray(selectedValues) && selectedValues.includes(String(value));
        const selClass = isSelected ?
            'bg-[var(--dfc-brand-primary)] text-[var(--dfc-brand-primary-contrast)]' :
            'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300';
        return `<button type="button" class="multi-select-choice-tag px-3 py-1 rounded-full text-sm font-medium ${selClass} hover:opacity-80 transition-opacity" data-field="${field}" data-value="${value}">${displayName}</button>`;
    }).join('');
}

/**
 * Renders selected values as removable tags.
 * @param {HTMLElement} container Target container.
 * @param {Array} allOptions Full option list.
 * @param {string} field Field name.
 * @param {Array<string>} selectedValues Selected values.
 * @param {string} displayProp Option display property.
 * @param {string} valueProp Option value property.
 * @returns {void}
 */
export function renderMultiSelectTags(container, allOptions, field, selectedValues, displayProp = 'name', valueProp = 'id') {
    if (!container || !Array.isArray(allOptions)) {return;}
    if (!Array.isArray(selectedValues)) {selectedValues = [];}
    container.innerHTML = selectedValues.map(value => {
        const option = allOptions.find(o => o && o[valueProp] === value);
        const displayName = option ? option[displayProp] : value;
        return `<span class="tag bg-gray-200 dark:bg-gray-600 dark:text-gray-200">${displayName}<button type="button" class="form-remove-tag-btn ml-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" data-field="${field}" data-value="${value}">&times;</button></span>`;
    }).join('');
}

/**
 * Wires multi-select controls inside the active form modal.
 * @param {string} type Current form type.
 * @param {object} currentItemData Mutable modal item data.
 * @returns {void}
 */
export function setupMultiSelectFields(type, currentItemData) {

    // --- Helper: Refined Multi-Select Renderer ---
    const renderRefinedMultiSelect = (containerId, optionsRAW, fieldKey, selectedIds, displayProp = 'name', idProp = 'id', colorProp = 'color', isRequired = false) => {
        const container = document.getElementById(containerId);
        if (!container) {return;}

        // 1. Normalize Options (Handle String Arrays like Providers)
        let options = [];
        if (Array.isArray(optionsRAW)) {
            options = optionsRAW.map(o => {
                if (typeof o === 'string') {return { [idProp]: o, [displayProp]: o };}
                return o;
            });
        }

        // Hide parent field wrapper if no options available
        if (!options || options.length === 0) {
            const parent = container.closest('div');
            if (parent) {parent.classList.add('hidden');}
            return;
        }

        // Ensure Data Array
        if (!Array.isArray(currentItemData[fieldKey])) {currentItemData[fieldKey] = [];}

        // Structure: Tags Row + Full Width Select
        // Tweaked spacing based on feedback: mb-1 (less space after), text-xs for tags
        container.innerHTML = `
            <div style="position: relative; width: 100%;">
                <div id="${containerId}-tags" class="flex flex-wrap gap-2 mb-1 min-h-[0px]"></div>
                <select id="${containerId}-select" class="w-full h-[34px] p-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 text-sm">
                </select>
                ${isRequired ? `<input type="text" id="${containerId}-required-hidden" value="${currentItemData[fieldKey]?.length > 0 ? 'valid' : ''}" style="position: absolute; opacity: 0; width: 1px; height: 1px; bottom: 0; left: 50%; z-index: -1; border: none; padding: 0; margin: 0; pointer-events: none;" required tabIndex="-1">` : ''}
            </div>
        `;

        const tagsContainer = document.getElementById(`${containerId}-tags`);
        const select = document.getElementById(`${containerId}-select`);

        const updateUI = () => {
            // 1. Render Tags
            tagsContainer.innerHTML = currentItemData[fieldKey].map(id => {
                const item = options.find(o => o[idProp] === id);
                const label = item ? item[displayProp] : id;

                // Styling Logic
                let styleAttr = '';
                // Changed: text-xs (smaller font), py-0.5 (smaller height)
                let classList = 'flex items-center px-2 py-0.5 text-xs rounded-full shadow-sm border ';

                if (item && item[colorProp]) {
                    // Admin Color Present - Use adaptive text color for accessibility
                    const bgColor = item[colorProp];
                    const textColor = isColorDark(bgColor) ? 'white' : '#1f2937'; // gray-800
                    styleAttr = `background-color: ${bgColor}; color: ${textColor}; border-color: transparent;`;
                } else {
                    // Default Fallback (Blue/Gray) - User-like styling
                    classList += 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800';
                }

                return `
                    <span class="${classList}" style="${styleAttr}">
                        ${label}
                        <button type="button" class="ml-1.5 hover:text-red-500 dark:hover:text-red-400 font-bold focus:outline-none remove-btn" data-id="${id}" style="color: inherit; opacity: 0.7;">×</button>
                    </span>
                `;
            }).join('');

            // 2. Render Select Options (Excluding already selected)
            const available = options.filter(o =>
                o.isAddable !== false &&
                !currentItemData[fieldKey].includes(o[idProp]),
            );
            // Intelligent Placeholder
            let placeholderRaw = fieldKey.replace(/_/g, ' ');
            if (placeholderRaw === 'types') {placeholderRaw = 'Type';}
            const placeholder = `+ Add ${placeholderRaw.charAt(0).toUpperCase() + placeholderRaw.slice(1)}`;

            select.innerHTML = `<option value="">${placeholder}</option>` +
                available.map(o => `<option value="${o[idProp]}">${o[displayProp]}</option>`).join('');

            if (available.length === 0) {
                select.innerHTML = `<option value="" disabled>All options selected</option>`;
            } else {
                select.value = "";
            }

            // 3. Handle Required Validation
            if (isRequired) {
                const hiddenInput = document.getElementById(`${containerId}-required-hidden`);
                if (hiddenInput) {
                    const hasSelected = currentItemData[fieldKey].length > 0;
                    hiddenInput.value = hasSelected ? 'valid' : '';
                    if (!hasSelected) {
                        hiddenInput.setCustomValidity('Please select at least one option.');
                    } else {
                        hiddenInput.setCustomValidity('');
                    }
                }
            }

            // Sync color
            if (typeof updateSelectStyles === 'function') {updateSelectStyles(select, type);}
            else if (select.value === '') {
                select.classList.add('text-gray-500', 'dark:text-gray-400');
                select.classList.remove('text-gray-900', 'dark:text-gray-200');
            }

            // 3. Bind Remove Events
            tagsContainer.querySelectorAll('.remove-btn').forEach(btn => {
                btn.onclick = (e) => {
                    e.preventDefault(); e.stopPropagation();
                    const idToRemove = btn.dataset.id;
                    const idx = currentItemData[fieldKey].indexOf(idToRemove);
                    if (idx > -1) {
                        currentItemData[fieldKey].splice(idx, 1);
                        updateUI();

                        // Special Logic: Remove Dynamic Fields for Types
                        if (fieldKey === 'types') {
                            const dynContainer = document.getElementById(`dynamic-fields-${idToRemove}`);
                            if (dynContainer) {
                                destroyRegisteredControlsIn(dynContainer);
                                dynContainer.remove();
                            }
                        }
                    }
                };
            });

            // 4. Update Select Color based on selection
            if (select.value === "") {
                select.classList.add('text-gray-500', 'dark:text-gray-400');
                select.classList.remove('text-gray-900', 'dark:text-gray-200');
            } else {
                select.classList.remove('text-gray-500', 'dark:text-gray-400');
                select.classList.add('text-gray-900', 'dark:text-gray-200');
            }
        };

        // Bind Add Event (OnChange)
        select.onchange = (e) => {
            const newId = e.target.value;
            if (newId && !currentItemData[fieldKey].includes(newId)) {
                currentItemData[fieldKey].push(newId);
                updateUI();

                // Special Logic: Add Dynamic Fields for Types
                if (fieldKey === 'types') {
                    const typeConfig = options.find(t => t.id === newId);
                    const itemForm = container.closest('form');
                    if (typeConfig && itemForm && typeof renderDynamicFieldForm === 'function') {
                        renderDynamicFieldForm(typeConfig, itemForm);
                    }
                }
            }
        };

        // Initial Render
        updateUI();

        // Initial Dynamic Fields Check (Edit Mode)
        if (fieldKey === 'types') {
            currentItemData[fieldKey].forEach(typeId => {
                const typeConfig = options.find(t => t.id === typeId);
                const itemForm = container.closest('form');
                if (typeConfig && itemForm && typeof renderDynamicFieldForm === 'function') {
                    renderDynamicFieldForm(typeConfig, itemForm);
                }
            });
        }
    };

    // --- Apply to Entities ---
    switch (type) {
        case 'patient':
            // Provider
            const providerChoices = document.getElementById('form-provider-choices');
            if (providerChoices) {
                renderRefinedMultiSelect(
                    'form-provider-choices',
                    getPatientProviderPickerOptions(),
                    'provider',
                    currentItemData.provider,
                );
            }
            // Types
            renderRefinedMultiSelect('form-patient-types-choices', getScopedTypeOptions(appData?.config?.patientTypes || [], currentItemData.types), 'types', currentItemData.types, 'name', 'id', 'color', true);
            break;

        case 'project':
            // Project Manager
            const pmChoices = document.getElementById('form-project_manager-choices');
            if (pmChoices) {
                const allowed = getSortedUsers(getAssignableUsersWithPermission('projects'));
                renderRefinedMultiSelect('form-project_manager-choices', allowed, 'project_manager', currentItemData.project_manager);
            }
            // Types
            renderRefinedMultiSelect('form-project-types-choices', getScopedTypeOptions(appData?.config?.projectTypes || [], currentItemData.types), 'types', currentItemData.types);
            break;

        case 'asset_service': case 'asset_issue':
            // Managed By
            const managedByChoices = document.getElementById('form-managed_by-choices');
            if (managedByChoices) {
                const allowed = getSortedUsers(getAssignableUsersWithPermission('assets'));
                renderRefinedMultiSelect('form-managed_by-choices', allowed, 'managed_by', currentItemData.managed_by);
            }
            break;

        case 'asset':
            // Types
            renderRefinedMultiSelect('form-asset-types-choices', getScopedTypeOptions(appData?.config?.assetTypes || [], currentItemData.types), 'types', currentItemData.types);
            break;

        case 'task':
            // Types
            renderRefinedMultiSelect('form-task-types-choices', getScopedTypeOptions(appData?.config?.taskTypes || [], currentItemData.types), 'types', currentItemData.types);
            updateTaskUserSelectors(
                getTaskAssignmentScopeFromParent(
                    currentItemData.parentId,
                    currentItemData.parentType,
                ),
            );
            break;
        default:
            break;
    }

    // --- Global: Date/Time Input Color Logic ---
    // Applies 'text-gray-500' if value is empty (visual placeholder effect), otherwise standard text color.
    // We attach this to all date/datetime-local inputs in the form logic that just rendered.
    // Since we don't have the form ref here easily, we search document-wide or within active modal.
    // A safe bet is checking all inputs, or just assume the modal is open.
    const dateInputs = document.querySelectorAll('input[type="date"], input[type="datetime-local"]');
    dateInputs.forEach(input => {
        const updateColor = () => {
            if (!input.value) {
                input.classList.add('text-gray-500', 'dark:text-gray-400');
                input.classList.remove('text-gray-900', 'dark:text-gray-200');
            } else {
                input.classList.remove('text-gray-500', 'dark:text-gray-400');
                input.classList.add('text-gray-900', 'dark:text-gray-200');
            }
        };
        input.addEventListener('change', updateColor);
        input.addEventListener('input', updateColor); // for immediate feedback
        updateColor(); // Init
    });
}
